<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { useAlert } from 'dashboard/composables';
import { useSalesPropertyDocuments } from '../composables/useSalesPropertyDocuments';
import SalesPartyDocumentNotice from './SalesPartyDocumentNotice.vue';
import SalesPropertyDocumentsSummary from './SalesPropertyDocumentsSummary.vue';
import SalesPropertyDocumentsUploader from './SalesPropertyDocumentsUploader.vue';

const props = defineProps({
  transactionId: { type: [Number, String], required: true },
  canEdit: { type: Boolean, default: false },
  detailsLabel: { type: String, default: '' },
  detailsIcon: { type: String, default: '' },
  detailsTest: { type: String, default: 'property-details-action' },
  errors: { type: Object, default: () => ({}) },
  summary: { type: Boolean, default: false },
  showGuidance: { type: Boolean, default: true },
});

const emit = defineEmits(['changed', 'details']);
const { t } = useI18n();
const documentState = useSalesPropertyDocuments({
  id: computed(() => props.transactionId),
  t,
  notify: useAlert,
});
const savingDocumentId = ref(null);
const notices = ref({ registry: true, legible: true });
const title = computed(() =>
  props.summary
    ? t('SALES_PROPERTY.SECTIONS.DOCUMENTS')
    : t('SALES_PROPERTY.DOCUMENTS.TITLE')
);

const dismiss = notice => {
  notices.value = { ...notices.value, [notice]: false };
};

const emitDocumentSummary = () => {
  const registryCount = documentState.documents.value.length;
  emit('changed', {
    registry_count: registryCount,
    registry_document_present: documentState.documents.value.some(
      document => document.registry_document
    ),
  });
};

const upload = async files => {
  if (await documentState.uploadFiles(files)) emitDocumentSummary();
};

const remove = async document => {
  if (await documentState.remove(document)) emitDocumentSummary();
};

const saveTypes = async (document, types) => {
  savingDocumentId.value = document.id;
  const updated = await documentState.updateTypes(document, types);
  savingDocumentId.value = null;
  if (updated) emitDocumentSummary();
};

const dismissPending = item => documentState.dismissPending(item);

onMounted(async () => {
  await documentState.load({ includeCatalog: !props.summary });
  emitDocumentSummary();
});
</script>

<template>
  <LaisCard
    :title="summary ? title : ''"
    :density="summary ? 'cozy' : 'flush'"
    :class="summary ? '' : 'self-start !border-0 !shadow-sm'"
    data-test="sales-property-documents-section"
  >
    <div :class="summary ? 'grid gap-4' : 'p-8 font-roboto'">
      <div v-if="!summary" data-test="sales-property-documents-header">
        <h2
          class="mb-2 font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:text-[24px] max-[700px]:leading-[29px]"
        >
          {{ t('SALES_PROPERTY.DOCUMENTS.TITLE') }}
        </h2>
        <p
          class="mb-[22px] w-[52vw] text-xl leading-6 text-[#464F53] max-[700px]:mb-[44px] max-[700px]:w-full"
          data-test="sales-property-documents-description"
        >
          {{ t('SALES_PROPERTY.DOCUMENTS.DESCRIPTION') }}
        </p>
      </div>
      <SalesPropertyDocumentsSummary
        v-if="summary"
        :documents="documentState.documents.value"
        :loading="documentState.loading.value"
        @download="documentState.download"
      />
      <SalesPropertyDocumentsUploader
        v-else
        :documents="documentState.documents.value"
        :pending="documentState.pending.value"
        :loading="documentState.loading.value"
        :can-upload="canEdit"
        :document-types="documentState.documentTypes.value"
        :saving-document-id="savingDocumentId"
        @files="upload"
        @download="documentState.download"
        @remove="remove"
        @save-types="saveTypes"
        @dismiss-pending="dismissPending"
      />
      <div v-if="showGuidance" class="grid gap-[5px]">
        <SalesPartyDocumentNotice
          v-if="notices.registry"
          data-test="property-notice-registry"
          tone="danger"
          @dismiss="dismiss('registry')"
        >
          {{ t('SALES_PROPERTY.DOCUMENTS.REQUIRED_PREFIX') }}
          <strong>{{ t('SALES_PROPERTY.DOCUMENTS.REQUIRED_ONUS') }}</strong>
          {{ t('SALES_PROPERTY.DOCUMENTS.REQUIRED_MIDDLE') }}
          <strong>{{ t('SALES_PROPERTY.DOCUMENTS.REQUIRED_PROPERTY') }}</strong>
          {{ t('SALES_PROPERTY.DOCUMENTS.REQUIRED_SUFFIX') }}
        </SalesPartyDocumentNotice>
        <SalesPartyDocumentNotice
          v-if="notices.legible"
          data-test="property-notice-legible"
          @dismiss="dismiss('legible')"
        >
          {{ t('SALES_PROPERTY.DOCUMENTS.LEGIBLE_PREFIX') }}
          <strong>{{ t('SALES_PROPERTY.DOCUMENTS.LEGIBLE_BOLD') }}</strong>
          {{ t('SALES_PROPERTY.DOCUMENTS.LEGIBLE_SUFFIX') }}
        </SalesPartyDocumentNotice>
      </div>
      <p v-if="errors.documents" class="text-xs text-lais-danger">
        {{ errors.documents }}
      </p>
      <slot name="footer" />
    </div>
    <template v-if="detailsLabel" #footer>
      <LaisPrimaryButton
        :data-test="detailsTest"
        :icon="detailsIcon"
        variant="link"
        size="sm"
        @click="emit('details')"
      >
        {{ detailsLabel }}
      </LaisPrimaryButton>
    </template>
  </LaisCard>
</template>
